import { Cell, ExcelRowDefinition, excelRowIntoObject, readExcelRowsFromClipboardHtmlContent } from '@app/utils/excelUtils';
import { convertReadErrorToExplanation, readHTMLContentFromClipboardDirectly } from '@app/utils/clipboardUtils';
import { showContentOnlyModal } from '@app/components/Modal';
import ErrorModal from '@app/components/ErrorModal';
import React from 'react';
import { COUNTRY_CODES } from '@app/domain/countries';
import { isIbanMandatory } from '@supplierRegistration/domain/supplierRegistrationAsyncInitiateValidationProcess';

export const handleReadFromClipboard = async (): Promise<null | SupplierRegistrationPastedRow> => {
  try {
    return applyValidatedPayeeExcelRowToForm(await readHTMLContentFromClipboardDirectly());
  } catch (e: unknown) {
    let modalErrorContent: string;

    if (e instanceof Error) {
      modalErrorContent = convertReadErrorToExplanation(e.message);
    } else {
      modalErrorContent = convertReadErrorToExplanation(null);
    }

    showContentOnlyModal((onDone) => (
      <ErrorModal headerContent='Paste Error' bodyContent={modalErrorContent} okButtonText='OK' onDone={onDone} />
    ));
    return null;
  }
};

const applyValidatedPayeeExcelRowToForm = (clipboardExcelRowsHtml: string | null): null | SupplierRegistrationPastedRow => {
  if (!clipboardExcelRowsHtml) {
    showContentOnlyModal((onDone) => (
      <ErrorModal
        headerContent='Paste Error'
        bodyContent='No suitable content found in clipboard'
        okButtonText='OK'
        onDone={onDone}
      />
    ));
    return null;
  }

  const rows = readExcelRowsFromClipboardHtmlContent(clipboardExcelRowsHtml);

  if (!rows.length) {
    showContentOnlyModal((onDone) => (
      <ErrorModal
        headerContent='Paste Error'
        bodyContent='Could not find excel rows in clipboard to fill the form'
        okButtonText='OK'
        onDone={onDone}
      />
    ));
    return null;
  }

  return excelRowIntoRegistration(rows[0]);
};

export function excelRowIntoRegistration(row: Cell[]): SupplierRegistrationPastedRow {
  const excelRow = excelRowIntoObject(row, supplierRegistrationExcelRowFieldToColumn) as SupplierRegistrationExcelRow;

  // The sheet has a single "accountNumber/IBAN" column, but for these countries the bank form renders only the IBAN input
  if (isIbanMandatory(excelRow.accountBankCountryCode as COUNTRY_CODES | undefined)) {
    return { ...excelRow, iban: excelRow.accountNumber, accountNumber: undefined };
  }

  return { ...excelRow, iban: undefined };
}

export type SupplierRegistrationPastedRow = SupplierRegistrationExcelRow & { iban: string | null | undefined };

export interface SupplierRegistrationExcelRow {
  registrationType: string | null | undefined;
  companyName: string | null | undefined;
  additionalCompanyName: string | null | undefined;
  companyAddressCountry: string | null | undefined;
  companyAddress: string | null | undefined;
  companyAddressCity: string | null | undefined;
  companyAddressState: string | null | undefined;
  companyAddressZipCode: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  leiType: string | null | undefined;
  leiTypeOtherValue: string | null | undefined;
  leiValue: string | null | undefined;
  phoneCountryCode: string | null | undefined;
  phoneNumber: string | null | undefined;
  email: string | null | undefined;
  country: string | null | undefined;
  website: string | null | undefined;
  referrer: string | null | undefined;
  hasAgreed: string | null | undefined;
  swiftCode: string | null | undefined;
  bankCode: string | null | undefined;
  branchCode: string | null | undefined;
  accountNumber: string | null | undefined;
  accountBankCountryCode: string | null | undefined;
  abaRouting: string | null | undefined;
  currency: string | null | undefined;
  furtherCredit: string | null | undefined;
  canUseAch: string | null | undefined;
  canUseEft: string | null | undefined;
  isIndividual: string | null | undefined;
  accountType: string | null | undefined;
  dateOfBirth: string | null | undefined;
  cnaps: string | null | undefined;
}

const supplierRegistrationExcelRowFieldToColumn: ExcelRowDefinition<SupplierRegistrationExcelRow> = {
  registrationType: 'A',
  companyName: 'B',
  additionalCompanyName: 'C',
  companyAddressCountry: 'D',
  companyAddress: 'E',
  companyAddressCity: 'F',
  companyAddressState: 'G',
  companyAddressZipCode: 'H',
  firstName: 'I',
  lastName: 'J',
  leiType: 'K',
  leiTypeOtherValue: 'L',
  leiValue: 'M',
  phoneCountryCode: 'N',
  phoneNumber: 'O',
  email: 'P',
  country: 'Q',
  website: 'R',
  referrer: 'S',
  hasAgreed: 'T',
  swiftCode: 'U',
  bankCode: 'V',
  branchCode: 'W',
  accountNumber: 'X',
  accountBankCountryCode: 'Y',
  abaRouting: 'Z',
  currency: 'AA',
  furtherCredit: 'AB',
  canUseAch: 'AC',
  canUseEft: 'AD',
  isIndividual: 'AE',
  accountType: 'AF',
  dateOfBirth: 'AG',
  cnaps: 'AH',
};
